import React, { useState } from 'react';
import { Database, TrendingUp, MapPin, Clock, AlertTriangle } from 'lucide-react';
import { useIntelStats, useHotspots, usePatterns } from '../hooks/useIntelData';

interface IntelTabProps {
  theme?: any;
  userLocation?: { lat: number; lng: number } | null;
}

const CRIME_COLORS: Record<string, string> = {
  HOMICIDE:      '#ff1a1a',
  ASSAULT:       '#ff4400',
  ROBBERY:       '#ff7700',
  WEAPONS:       '#ff9900',
  SEX_CRIME:     '#ff5566',
  ARSON:         '#ff6600',
  VEHICLE_THEFT: '#ffcc00',
  BURGLARY:      '#ffaa00',
  DRUG:          '#88aaff',
  DUI:           '#aaaaff',
  THEFT:         '#00e676',
  VANDALISM:     '#44dd88',
  OTHER:         '#556677',
  FRAUD:         '#ba8cff',
  LIQUOR:        '#d4af37',
  DISORDER:      '#9999aa',
  TRAFFIC:       '#5e9ed6',
  DOMESTIC:      '#ff6b9d',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => (i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`));

type FilterType = 'ALL' | 'VEHICLE_THEFT' | 'ASSAULT' | 'HOMICIDE' | 'ROBBERY' | 'BURGLARY' | 'DRUG';

const FILTERS: { key: FilterType; label: string }[] = [
  { key: 'ALL',           label: 'ALL'      },
  { key: 'VEHICLE_THEFT', label: 'CAR THEFT'},
  { key: 'ASSAULT',       label: 'ASSAULT'  },
  { key: 'HOMICIDE',      label: 'HOMICIDE' },
  { key: 'ROBBERY',       label: 'ROBBERY'  },
  { key: 'BURGLARY',      label: 'BURGLARY' },
  { key: 'DRUG',          label: 'DRUG'     },
];

export default function IntelTab({ theme, userLocation }: IntelTabProps) {
  const [filter, setFilter] = useState<FilterType>('ALL');

  const { stats, loading: statsLoading } = useIntelStats();
  const { hotspots, loading: hotspotsLoading } = useHotspots(filter);
  const { patterns, loading: patternsLoading } = usePatterns(
    userLocation?.lat ?? null,
    userLocation?.lng ?? null,
  );

  const totalCrimes = stats?.total ?? 0;

  // Sort categories by count for the breakdown bar
  // Defensive: API may omit byCategory or return it in a different shape
  const topCategories = stats && stats.byCategory && typeof stats.byCategory === 'object'
    ? Object.entries(stats.byCategory)
        .filter(([, v]) => typeof v === 'number')
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .slice(0, 8)
    : [];

  // Find peak hour and peak day from patterns
  const peakHour = patterns
    ? patterns.hourly.indexOf(Math.max(...patterns.hourly))
    : null;
  const peakDay = patterns
    ? patterns.daily.indexOf(Math.max(...patterns.daily))
    : null;

  return (
    <div className="p-3 space-y-4 text-amber-600 h-full overflow-y-auto font-mono text-xs">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Database size={16} className="text-amber-500" />
        <span className="font-orbitron text-sm font-bold text-amber-500 tracking-widest">NOBLE GLITCH INTEL</span>
        {(statsLoading || hotspotsLoading) && (
          <span className="text-amber-800 animate-pulse">LOADING…</span>
        )}
      </div>

      {/* Crime totals */}
      {stats && (
        <div className="bg-black border border-amber-900/50 rounded-sm p-3">
          <div className="text-amber-700 uppercase tracking-widest text-xs mb-2">TPD Crime Database</div>
          <div className="text-2xl font-bold text-amber-400 font-orbitron">
            {totalCrimes.toLocaleString()}
          </div>
          <div className="text-amber-700">incidents on record</div>

          {/* Category breakdown bars */}
          <div className="mt-3 space-y-1">
            {topCategories.map(([cat, count]) => {
              const pct = totalCrimes ? Math.round(((count as number) / totalCrimes) * 100) : 0;
              return (
                <div key={cat} className="flex items-center gap-2">
                  <div className="w-20 truncate" style={{ color: CRIME_COLORS[cat] ?? '#888' }}>
                    {cat.replace(/_/g, ' ')}
                  </div>
                  <div className="flex-1 bg-amber-950/40 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: `${pct}%`, backgroundColor: CRIME_COLORS[cat] ?? '#888' }}
                    />
                  </div>
                  <div className="w-8 text-right text-amber-700">{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Crime type filter */}
      <div>
        <div className="text-amber-700 uppercase tracking-widest mb-2">Filter by Type</div>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className="px-2 py-0.5 rounded-sm text-xs font-mono border transition-colors"
              style={{
                borderColor: filter === f.key ? '#d4a843' : 'rgba(180,120,0,0.3)',
                color: filter === f.key ? '#d4a843' : '#6b5000',
                backgroundColor: filter === f.key ? 'rgba(212,168,67,0.1)' : 'transparent',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top hotspots */}
      <div className="bg-black border border-amber-900/50 rounded-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <MapPin size={12} className="text-amber-500" />
          <span className="text-amber-700 uppercase tracking-widest">Top Hotspots in Tucson</span>
        </div>
        {hotspotsLoading && <div className="text-amber-900 animate-pulse">Loading…</div>}
        {!hotspotsLoading && hotspots.length === 0 && (
          <div className="text-amber-900">No hotspot data yet — crime data may still be ingesting.</div>
        )}
        <div className="space-y-2">
          {hotspots.slice(0, 10).map((h, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-amber-700 w-4 shrink-0">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span style={{ color: CRIME_COLORS[(h.topCategory ?? h.topType ?? "OTHER")] ?? '#d4a843' }} className="font-bold uppercase">
                    {(h.topCategory ?? h.topType ?? "OTHER").replace(/_/g, ' ')}
                  </span>
                  <span className="text-amber-700">{h.count} incidents</span>
                </div>
                <div className="text-amber-900 text-xs">
                  {h.lat.toFixed(4)}, {h.lon.toFixed(4)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Area patterns (if user location set) */}
      {userLocation && (
        <div className="bg-black border border-amber-900/50 rounded-sm p-3">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={12} className="text-amber-500" />
            <span className="text-amber-700 uppercase tracking-widest">Your Area Patterns (1mi)</span>
          </div>
          {patternsLoading && <div className="text-amber-900 animate-pulse">Analyzing…</div>}
          {patterns && (
            <>
              {peakHour !== null && (
                <div className="mb-1">
                  <span className="text-amber-700">Peak hour: </span>
                  <span className="text-amber-400">{HOURS[peakHour]}</span>
                </div>
              )}
              {peakDay !== null && (
                <div className="mb-2">
                  <span className="text-amber-700">Peak day: </span>
                  <span className="text-amber-400">{DAYS[peakDay]}</span>
                </div>
              )}

              {/* Hourly heatbar */}
              <div className="mb-1 text-amber-800">By hour:</div>
              <div className="flex gap-px h-6">
                {patterns.hourly.map((v, i) => {
                  const max = Math.max(...patterns.hourly, 1);
                  const pct = v / max;
                  return (
                    <div key={i} className="flex-1 flex items-end" title={`${HOURS[i]}: ${v}`}>
                      <div
                        className="w-full rounded-sm"
                        style={{ height: `${Math.max(2, pct * 100)}%`, backgroundColor: `rgba(212,168,67,${0.2 + pct * 0.8})` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-amber-900 mt-0.5">
                <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
              </div>

              {/* Top crime types in area */}
              {patterns.topTypes.length > 0 && (
                <div className="mt-2 space-y-1">
                  {patterns.topTypes.slice(0, 4).map(t => (
                    <div key={t.type} className="flex items-center gap-2">
                      <AlertTriangle size={10} style={{ color: CRIME_COLORS[t.type] ?? '#888' }} />
                      <span style={{ color: CRIME_COLORS[t.type] ?? '#888' }}>
                        {t.type.replace(/_/g, ' ')}
                      </span>
                      <span className="text-amber-800 ml-auto">{t.pct}%</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-2 text-amber-900">
                {patterns.total} incidents in this area (past year)
              </div>
            </>
          )}
          {!patternsLoading && !patterns && (
            <div className="text-amber-900">No crime history for this area.</div>
          )}
        </div>
      )}

      {!userLocation && (
        <div className="bg-black border border-amber-900/50 rounded-sm p-3 text-amber-900">
          <TrendingUp size={12} className="inline mr-1" />
          Set your location to see area crime patterns.
        </div>
      )}
    </div>
  );
}
